import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import clsx from "clsx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Typography from "@material-ui/core/Typography";
import MenuIcon from "@material-ui/icons/Menu";
import MenuOpen from "@material-ui/icons/MenuOpen";
import NavigateNext from "@material-ui/icons/NavigateNext";

import componentStyles from "assets/theme/components/sidebar.js";
import { IsSmall } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  menuOption: {
    display: "flex",
    alignItems: "center",
    fontSize: "1rem",
  },
});

export default function Sidebar({ routes, isOpen, toggleSidebar, onClose }) {
  const isMobile = IsSmall();
  const classes = { ...useStyles(), ...useCustomStyles() };
  const location = useLocation();
  const [state, setState] = useState({});

  const smallSidebar = isMobile ? false : isOpen;

  useEffect(() => {
    setState(getCollapseStates(routes));
  }, []);

  const getCollapseStates = (routes) => {
    let initialState = {};
    routes?.map((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname.indexOf(routes[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };

  const createLinks = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.redirect || prop.invisible) {
        return null;
      } else if (prop.divider) {
        return <Divider key={key} />;
      } else if (prop.title) {
        if (smallSidebar) {
          return null;
        }
        return (
          <Typography
            key={key}
            variant="h6"
            component="h6"
            classes={{ root: classes.title }}
          >
            {prop.title}
          </Typography>
        );
      }

      const textContent = (
        <>
          <Box
            minWidth="2.25rem"
            display="flex"
            alignItems="center"
            justifyContent={prop.collapse ? "flex-start" : "center"}
            style={{
              marginRight: smallSidebar ? "0.25rem" : 0,
            }}
          >
            <Box
              component={prop.icon}
              width="1.25rem!important"
              height="1.25rem!important"
              marginLeft={isOpen ? "-.25rem" : ""}
              className={classes["text" + prop.iconColor]}
            />
          </Box>
          {!smallSidebar && prop.name}
        </>
      );

      if (prop.collapse) {
        const st = {};
        st[prop["state"]] = !state[prop.state];
        if (prop.multiStates) {
          prop.multiStates?.forEach((item) => {
            st[item] = state[item];
          });
        }
        return (
          <div key={key}>
            <ListItem
              component={"a"}
              href="#mui"
              onClick={(e) => {
                e.preventDefault();
                setState(st);
              }}
              classes={{
                root: clsx(classes.listItemRoot, classes.menuOption, {
                  [classes.listItemRootCollapseActive]: getCollapseInitialState(
                    prop.views
                  ),
                }),
              }}
            >
              {textContent}
              {smallSidebar ? null : (
                <Box
                  component={NavigateNext}
                  marginLeft="auto"
                  width="1rem!important"
                  height="1rem!important"
                  className={clsx(classes.listItemRootCollapseIcon, {
                    [classes.listItemRootCollapseActiveIcon]: state[prop.state],
                  })}
                />
              )}
            </ListItem>
            <Collapse
              in={state[prop.state]}
              unmountOnExit
              className={classes.collapseRoot}
            >
              <List classes={{ root: classes.listRootCollapse }}>
                {createLinks(prop.views)}
              </List>
            </Collapse>
          </div>
        );
      } else if (prop.href) {
        return (
          <ListItem
            key={key}
            component={"a"}
            href={prop.href}
            onClick={isOpen && onClose}
            classes={{
              root:
                classes.listItemRoot +
                (prop.upgradeToPro
                  ? " " + classes.listItemRootUpgradeToPro
                  : ""),
              selected: classes.listItemSelected,
            }}
            target="_blank"
            selected={prop.upgradeToPro === true}
          >
            {textContent}
          </ListItem>
        );
      } else {
        return (
          <ListItem
            key={key}
            component={Link}
            onClick={isOpen && onClose}
            to={prop.layout + prop.path}
            classes={{
              root:
                classes.listItemRoot +
                (prop.upgradeToPro
                  ? " " + classes.listItemRootUpgradeToPro
                  : ""),
              selected: classes.listItemSelected,
            }}
            selected={
              location.pathname === prop.layout + prop.path ||
              prop.upgradeToPro === true
            }
          >
            {textContent}
          </ListItem>
        );
      }
    });
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="left"
      classes={{
        paper: clsx({
          [classes.drawerDockedMiniActive]: smallSidebar,
        }),
        docked: clsx({
          [classes.drawerPaperMiniActive]: smallSidebar,
        }),
      }}
      open={isOpen}
      onClose={onClose}
    >
      <PerfectScrollbar>
        <Box
          paddingLeft={smallSidebar ? "0" : "0.5rem"}
          display="flex"
          justifyContent={smallSidebar ? "center" : "flex-start"}
        >
          <IconButton onClick={toggleSidebar}>
            <Box
              component={!smallSidebar ? MenuOpen : MenuIcon}
              width="1.5rem!important"
              height="1.5rem!important"
            />
          </IconButton>
        </Box>
        <List classes={{ root: classes.listRoot }}>{createLinks(routes)}</List>
      </PerfectScrollbar>
    </Drawer>
  );
}
